import { mediaList } from "@/mocks/media";
import type { MediaItem, MediaType } from "@/types/media";

export interface FilterOptions {
  genre?: string;
  year?: number;
  region?: string;
  season?: number;
  sort?: "updated" | "heat" | "rating";
}

export const PAGE_SIZE = 8;

export function getMediaById(id: string | undefined): MediaItem | undefined {
  if (!id) return undefined;
  return mediaList.find((item) => item.id === id);
}

export function getMediaByType(type: MediaType): MediaItem[] {
  return mediaList.filter((item) => item.type === type);
}

export function filterMedia(items: MediaItem[], options: FilterOptions): MediaItem[] {
  const { genre, year, region, season, sort = "updated" } = options;
  const result = items.filter((item) => {
    if (genre && !item.genres.includes(genre)) return false;
    if (year && item.year !== year) return false;
    if (region && item.region !== region) return false;
    if (season && item.seasons !== season) return false;
    return true;
  });

  return [...result].sort((a, b) => {
    if (sort === "heat") return b.heat - a.heat;
    if (sort === "rating") return b.rating - a.rating;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

export function searchMedia(keyword: string): MediaItem[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return mediaList.filter((item) => {
    const haystack = [item.title, item.director, item.region, ...item.genres, ...item.cast]
      .join(" ")
      .toLowerCase();
    return haystack.includes(key);
  });
}

export function getRelated(item: MediaItem, count = 6): MediaItem[] {
  const pool = mediaList.filter((other) => other.id !== item.id);
  const sameGenreAndType = pool.filter((o) => o.type === item.type && o.genres.some((g) => item.genres.includes(g)));
  const sameType = pool.filter((o) => o.type === item.type);
  const rest = pool.filter((o) => o.type !== item.type);

  const seen = new Set<string>();
  const merged: MediaItem[] = [];
  [...sameGenreAndType, ...sameType, ...rest].forEach((o) => {
    if (!seen.has(o.id)) {
      seen.add(o.id);
      merged.push(o);
    }
  });
  return merged.slice(0, count);
}

export function getFeatured(): MediaItem[] {
  return ["m1", "m3", "t1", "m12", "m8"]
    .map((id) => getMediaById(id))
    .filter((item): item is MediaItem => Boolean(item));
}

export function getLatest(type?: MediaType, count = 8): MediaItem[] {
  const pool = type ? getMediaByType(type) : mediaList;
  return [...pool]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, count);
}

export function getTopRated(type?: MediaType, count = 6): MediaItem[] {
  const pool = type ? getMediaByType(type) : mediaList;
  return [...pool].sort((a, b) => b.rating - a.rating).slice(0, count);
}

export function formatDate(value: string): string {
  const d = new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}